import React, { useEffect, useRef } from 'react';

const SHADES = '.,-~:;=!*#$@';
const DONUT_VARIANTS = {
  default: {
    frame: {
      bufferWidth: 80,
      bufferHeight: 22,
      centerX: 40,
      centerY: 12,
      scaleX: 30,
      scaleY: 15,
    },
    maxWidthClassName: 'max-w-xl',
    viewportClassName: 'min-h-[220px] px-3 py-5 sm:min-h-[250px]',
    preClassName:
      'text-[5px] leading-[5px] text-blue-200 sm:text-[6px] sm:leading-[6px] md:text-[7px] md:leading-[7px]',
  },
  expanded: {
    frame: {
      bufferWidth: 108,
      bufferHeight: 30,
      centerX: 54,
      centerY: 16,
      scaleX: 44,
      scaleY: 22,
    },
    maxWidthClassName: 'max-w-2xl',
    viewportClassName: 'min-h-[280px] px-3 py-5 sm:min-h-[300px] md:min-h-[320px]',
    preClassName:
      'text-[7px] leading-[7px] text-blue-100 sm:text-[8px] sm:leading-[8px] md:text-[9px] md:leading-[9px]',
  },
};

const renderFrame = (A, B, frameConfig) => {
  const { bufferWidth, bufferHeight, centerX, centerY, scaleX, scaleY } = frameConfig;
  const bufferSize = bufferWidth * bufferHeight;
  const output = new Array(bufferSize);
  const zBuffer = new Array(bufferSize).fill(0);

  for (let k = 0; k < bufferSize; k += 1) {
    output[k] = k % bufferWidth === bufferWidth - 1 ? '\n' : ' ';
  }

  const cA = Math.cos(A);
  const sA = Math.sin(A);
  const cB = Math.cos(B);
  const sB = Math.sin(B);

  for (let j = 0; j < 6.28; j += 0.07) {
    const ct = Math.cos(j);
    const st = Math.sin(j);

    for (let i = 0; i < 6.28; i += 0.02) {
      const sp = Math.sin(i);
      const cp = Math.cos(i);
      const h = ct + 2;
      const D = 1 / (sp * h * sA + st * cA + 5);
      const t = sp * h * cA - st * sA;

      const x = (centerX + scaleX * D * (cp * h * cB - t * sB)) | 0;
      const y = (centerY + scaleY * D * (cp * h * sB + t * cB)) | 0;
      const o = x + bufferWidth * y;
      const N = (8 * ((st * sA - sp * ct * cA) * cB - sp * ct * sA - st * cA - cp * ct * sB)) | 0;

      if (y < bufferHeight && y >= 0 && x >= 0 && x < bufferWidth - 1 && D > zBuffer[o]) {
        zBuffer[o] = D;
        const shadeIndex = Math.max(0, Math.min(SHADES.length - 1, N));
        output[o] = SHADES[shadeIndex];
      }
    }
  }

  return output.join('');
};

const AsciiDonut = ({ size = 'default' }) => {
  const variant = DONUT_VARIANTS[size] || DONUT_VARIANTS.default;
  const preRef = useRef(null);
  const anglesRef = useRef({ A: 1, B: 1 });

  useEffect(() => {
    let intervalId;
    let mediaQueryList;
    let mediaListener;

    const draw = (animate = true) => {
      const node = preRef.current;
      if (!node) {
        return;
      }

      if (animate) {
        anglesRef.current.A += 0.07;
        anglesRef.current.B += 0.03;
      }

      node.textContent = renderFrame(anglesRef.current.A, anglesRef.current.B, variant.frame);
    };

    const start = () => {
      draw(true);
      intervalId = window.setInterval(() => draw(true), 50);
    };

    const stop = () => {
      if (intervalId) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    const syncMotionPreference = () => {
      stop();
      if (mediaQueryList?.matches) {
        draw(false);
        return;
      }
      start();
    };

    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      mediaQueryList = window.matchMedia('(prefers-reduced-motion: reduce)');
      mediaListener = () => syncMotionPreference();

      if (typeof mediaQueryList.addEventListener === 'function') {
        mediaQueryList.addEventListener('change', mediaListener);
      } else if (typeof mediaQueryList.addListener === 'function') {
        mediaQueryList.addListener(mediaListener);
      }
    }

    syncMotionPreference();

    return () => {
      stop();

      if (mediaQueryList && mediaListener) {
        if (typeof mediaQueryList.removeEventListener === 'function') {
          mediaQueryList.removeEventListener('change', mediaListener);
        } else if (typeof mediaQueryList.removeListener === 'function') {
          mediaQueryList.removeListener(mediaListener);
        }
      }
    };
  }, [variant.frame]);

  return (
    <aside
      className={`w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-lg site-card ${variant.maxWidthClassName}`}
    >
      <div className="mb-3 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">Visual</p>
          <p className="text-sm font-semibold text-gray-900">donut.c tribute</p>
        </div>
        <span className="rounded-full border border-gray-200 bg-gray-50 site-card-soft px-3 py-1 text-xs text-gray-600">
          ASCII + math
        </span>
      </div>

      <div
        className={`flex items-center justify-center rounded-xl bg-slate-950 shadow-inner ${variant.viewportClassName}`}
      >
        <p className="sr-only">Animacao decorativa de um donut ASCII girando.</p>
        <pre
          ref={preRef}
          aria-hidden="true"
          className={`m-0 overflow-hidden ${variant.preClassName}`}
        />
      </div>

      <p className="mt-3 text-sm text-gray-600">
        Inspirado no classico donut em ASCII renderizado com z-buffer, perspectiva e luminancia em
        tempo real.
      </p>
    </aside>
  );
};

export default AsciiDonut;
